import { useMemo } from 'react';
import { SearchContext } from '../types';

interface LoadingStatesInput {
  // Setup/Configuration Loading
  creatorDiscoverySettingsLoading: boolean;
  defaultFiltersResolved: boolean;

  // Search Operations Loading
  fetching: boolean;
  isGeneratingFilter: boolean;

  // Content/Pagination Loading
  isPaginationFetching: boolean;

  // Detail View Loading
  detailViewFetching?: boolean;

  // Context-specific
  searchContext: SearchContext | null;
  queryVariables: {
    username?: string;
    creatorUsername?: string;
  };

  // Data state
  hasSearchResults?: boolean;
  hasSearchFilters?: boolean;
}

export interface CategorizedLoadingStates {
  // Primary Categories
  setupLoading: {
    isLoading: boolean;
    reasons: string[];
  };
  searchLoading: {
    isLoading: boolean;
    reasons: string[];
  };
  contentLoading: {
    isLoading: boolean;
    reasons: string[];
  };
  detailLoading: {
    isLoading: boolean;
    reasons: string[];
  };

  // Composite States
  overallLoading: {
    isLoading: boolean;
    primaryCategory: 'setup' | 'search' | 'content' | 'detail' | 'none';
    activeOperations: string[];
  };

  // UI States
  uiStates: {
    shouldShowMainSpinner: boolean;
    shouldShowRefreshSpinner: boolean;
    shouldShowPaginationLoader: boolean;
    shouldShowSearchInputSpinner: boolean;
    shouldShowFiltersLoading: boolean;
    shouldShowEmptyState: boolean;
    shouldDisableInteractions: boolean;
  };
}

export const useLoadingStates = (input: LoadingStatesInput): CategorizedLoadingStates => {
  // Setup Loading - Things that need to load before the app can function properly
  const setupLoading = useMemo(() => ({
    isLoading: input.creatorDiscoverySettingsLoading || !input.defaultFiltersResolved,
    reasons: [
      ...(input.creatorDiscoverySettingsLoading ? ['Creator discovery settings loading'] : []),
      ...(!input.defaultFiltersResolved ? ['Default filters not resolved'] : []),
    ],
  }), [input.creatorDiscoverySettingsLoading, input.defaultFiltersResolved]);

  // Search Loading - Active search operations
  const searchLoading = useMemo(() => ({
    isLoading: input.fetching || input.isGeneratingFilter,
    reasons: [
      ...(input.fetching ? ['Fetching search results'] : []),
      ...(input.isGeneratingFilter ? ['Generating AI filter suggestions'] : []),
    ],
  }), [input.fetching, input.isGeneratingFilter]);

  // Content Loading - Loading more content or pagination
  const contentLoading = useMemo(() => ({
    isLoading: input.isPaginationFetching,
    reasons: [
      ...(input.isPaginationFetching ? ['Loading pagination results'] : []),
    ],
  }), [input.isPaginationFetching]);

  // Detail Loading - Loading detail view
  const detailLoading = useMemo(() => ({
    isLoading: input.detailViewFetching,
    reasons: [
      ...(input.detailViewFetching ? ['Loading detail view'] : []),
    ],
  }), [input.detailViewFetching]);

  // Determine primary loading category
  const overallLoading = useMemo(() => {
    let primaryCategory: 'setup' | 'search' | 'content' | 'detail' | 'none' = 'none';
    const activeOperations: string[] = [];

    if (setupLoading.isLoading) {
      primaryCategory = 'setup';
      activeOperations.push(...setupLoading.reasons);
    } else if (searchLoading.isLoading) {
      primaryCategory = 'search';
      activeOperations.push(...searchLoading.reasons);
    } else if (contentLoading.isLoading) {
      primaryCategory = 'content';
      activeOperations.push(...contentLoading.reasons);
    } else if (detailLoading.isLoading) {
      primaryCategory = 'detail';
      activeOperations.push(...detailLoading.reasons);
    }

    return {
      isLoading: setupLoading.isLoading || searchLoading.isLoading || contentLoading.isLoading || detailLoading.isLoading,
      primaryCategory,
      activeOperations,
    };
  }, [setupLoading, searchLoading, contentLoading, detailLoading]);

  // UI State Decisions
  const uiStates = useMemo(() => {
    // Check if we've actually performed a search that could return results
    const hasPerformedSearch = input.defaultFiltersResolved && !setupLoading.isLoading;

    return {
      // Show main spinner for setup or initial search loading
      shouldShowMainSpinner: setupLoading.isLoading || (searchLoading.isLoading && !contentLoading.isLoading),

      // Show refresh spinner for any non-setup loading
      shouldShowRefreshSpinner: searchLoading.isLoading || contentLoading.isLoading,

      // Show pagination loader only for pagination operations
      shouldShowPaginationLoader: contentLoading.isLoading,

      // Show search input spinner for AI generation in aspire-ai context
      shouldShowSearchInputSpinner: input.isGeneratingFilter && input.searchContext === 'aspire-ai',

      // Show filters loading when AI is generating filters
      shouldShowFiltersLoading: input.isGeneratingFilter,

      // Only show empty state after we've completed setup and search, and have no results
      shouldShowEmptyState: hasPerformedSearch &&
                           !searchLoading.isLoading &&
                           !contentLoading.isLoading &&
                           !input.hasSearchResults,

      // Disable interactions during critical loading operations
      shouldDisableInteractions: setupLoading.isLoading || searchLoading.isLoading,
    };
  }, [
    setupLoading.isLoading,
    searchLoading.isLoading,
    contentLoading.isLoading,
    input.isGeneratingFilter,
    input.searchContext,
    input.defaultFiltersResolved,
    input.hasSearchResults
  ]);

  return useMemo(() => ({
    setupLoading,
    searchLoading,
    contentLoading,
    detailLoading,
    overallLoading,
    uiStates,
  }), [setupLoading, searchLoading, contentLoading, detailLoading, overallLoading, uiStates]);
};
